import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    q: 'What is a foreign TikTok account?',
    a: 'A foreign TikTok account is an account registered and set up in a country like the UK, USA, Canada, etc. instead of Nigeria. This helps your videos reach a global audience, leading to higher engagement and better monetization opportunities.',
  },
  {
    q: 'Why do I need a foreign TikTok account?',
    a: 'Foreign accounts have access to features like TikTok Creator Fund, higher CPM rates, and better algorithmic reach. Many creators use them to grow faster and earn more from their content.',
  },
  {
    q: 'How long does it take to get my account?',
    a: 'We typically create and deliver your account within 24 hours after payment confirmation. You\'ll receive your login details via email.',
  },
  {
    q: 'Will I get banned for using a foreign account?',
    a: 'When set up properly (which we handle for you), foreign accounts are safe to use. We guide you on best practices to avoid any issues, including using the right VPN settings and account behavior.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept card payments and bank transfers via Flutterwave. The payment process is secure and straightforward — you\'ll be prompted to complete payment in a secure popup after placing your order.',
  },
  {
    q: 'Can I choose which country my account is from?',
    a: 'Yes! You can choose from several countries including UK, USA, Canada, Germany, France, Japan, Australia, and Brazil.',
  },
  {
    q: 'What if I\'m not satisfied with the service?',
    a: 'We offer a full refund if we cannot deliver your account within 48 hours. Please check our Refund Policy for more details.',
  },
  {
    q: 'Do I need to provide my own email?',
    a: 'Yes, you\'ll need to provide a new email address that has never been used on TikTok. This will be used to create your account.',
  },
  {
    q: 'Can I change my username later?',
    a: 'Yes, you can change your username anytime in TikTok settings. However, we recommend keeping it for at least 30 days after account creation.',
  },
  {
    q: 'How do I track my order?',
    a: 'You can track your order status anytime by visiting our Track Order page and entering your email address.',
  },
];

const FAQItem = ({ faq, isOpen, onClick }) => (
  <div className="border border-gray-800/60 rounded-2xl overflow-hidden">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-800/20 transition-colors"
    >
      <span className="text-white font-medium pr-4">{faq.q}</span>
      <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
        <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="px-5 pb-5 text-gray-400 text-sm leading-relaxed border-t border-gray-800/60 pt-4">
            {faq.a}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-16 px-4">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-4">
            <HelpCircle size={14} className="text-primary-400" />
            <span className="text-sm text-primary-300">FAQ</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Frequently Asked Questions</h1>
          <p className="text-gray-400">Everything you need to know about our service</p>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="space-y-3">
          {faqs.map((faq, i) => (
            <FAQItem
              key={i}
              faq={faq}
              isOpen={openIndex === i}
              onClick={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-12 bg-gray-900/50 border border-gray-800/60 rounded-2xl p-8"
        >
          <h3 className="text-xl font-bold text-white mb-2">Still have questions?</h3>
          <p className="text-gray-400 mb-6">Can't find what you're looking for? Contact our support team.</p>
          <Link
            to="/support"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 text-white font-semibold rounded-xl transition-all"
          >
            <MessageCircle size={18} /> Contact Support
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
